import { z } from 'zod';
const uuidSchema = z.string().uuid();
const dateSchema = z.string().date();
const dateTimeSchema = z.string().datetime({ offset: true });
/** Versioned boundary for source domains that register immutable DMS artifacts. */
export const dmsContractVersionSchema = z.literal('1');
export const dmsDocumentStateSchema = z.enum(['issued', 'voided', 'archived']);
export const dmsDocumentFamilySchema = z.enum([
    'settlement',
    'consumer',
    'inventory',
    'production',
    'safety',
    'publication',
    'export',
]);
export const canteenDmsDocumentDefinitionSchema = z.object({
    code: z.string().regex(/^canteen\.[a-z0-9_]+\.v1$/),
    name: z.string().min(1).max(180),
    family: dmsDocumentFamilySchema,
    legacyCodes: z.array(z.string().min(1)).default([]),
    description: z.string().min(1).max(500),
    retentionYears: z.number().int().min(1).max(100),
});
/**
 * A single catalog is the compatibility boundary for the historical Stołówka
 * prints.  The former KGK-CAN-CU-1 collision is intentionally represented by
 * two distinct stable codes, while retaining the source alias for migration.
 */
export const canteenDmsDocumentDefinitions = [
    {
        code: 'canteen.period_settlement.v1',
        name: 'Rozliczenie wyżywienia za okres',
        family: 'settlement',
        legacyCodes: ['KGK-CAN-CC-1'],
        description: 'Rozliczenie rezerwacji, anulowań, wydań i należności.',
        retentionYears: 5,
    },
    {
        code: 'canteen.consumer_register.v1',
        name: 'Wykaz osób korzystających z wyżywienia',
        family: 'consumer',
        legacyCodes: ['KGK-CAN-CU-1'],
        description: 'Wykaz osób, posiłków i ilości w wybranym okresie.',
        retentionYears: 5,
    },
    {
        code: 'canteen.invoice_variance.v1',
        name: 'Odchylenia dokumentów i faktur',
        family: 'inventory',
        legacyCodes: ['KGK-CAN-IV-1'],
        description: 'Porównanie wartości zadeklarowanych i wyliczonych faktur.',
        retentionYears: 5,
    },
    {
        code: 'canteen.meal_cancellation.v1',
        name: 'Rezygnacje z posiłków',
        family: 'consumer',
        legacyCodes: ['KGK-CAN-CU-1'],
        description: 'Rezygnacje rozdzielone od wykazu użytkowników mimo dawnej kolizji kodu.',
        retentionYears: 5,
    },
    {
        code: 'canteen.meal_issuance_settlement.v1',
        name: 'Rozliczenie wydanych posiłków',
        family: 'settlement',
        legacyCodes: ['KGK-CAN-MS-1'],
        description: 'Wydania, korekty i różnice względem rezerwacji.',
        retentionYears: 5,
    },
    {
        code: 'canteen.consumer_provisioning_detail.v1',
        name: 'Szczegółowe zaprowiantowanie użytkowników',
        family: 'consumer',
        legacyCodes: ['KGK-CAN-MUD-1'],
        description: 'Szczegóły świadczeń per osoba i posiłek.',
        retentionYears: 5,
    },
    {
        code: 'canteen.food_requisition.v1',
        name: 'Karta zapotrzebowania żywnościowego',
        family: 'inventory',
        legacyCodes: ['KGK-CAN-OC-1'],
        description: 'Zapotrzebowanie, zatwierdzenie i wydanie produktów.',
        retentionYears: 5,
    },
    {
        code: 'canteen.product_card.v1',
        name: 'Karta produktu',
        family: 'inventory',
        legacyCodes: ['KGK-CAN-PC-1'],
        description: 'Produkt, jednostka, grupa, alergeny i wartości odżywcze.',
        retentionYears: 5,
    },
    {
        code: 'canteen.stock_turnover_by_index.v1',
        name: 'Obroty magazynowe według indeksów',
        family: 'inventory',
        legacyCodes: ['KGK-CAN-SIO-1'],
        description: 'Przychody, rozchody i wartości według produktu.',
        retentionYears: 5,
    },
    {
        code: 'canteen.stock_turnover_by_group.v1',
        name: 'Obroty magazynowe według grup',
        family: 'inventory',
        legacyCodes: ['KGK-CAN-SIO-G-1'],
        description: 'Przychody, rozchody i wartości według hierarchii grup.',
        retentionYears: 5,
    },
    {
        code: 'canteen.stock_on_hand.v1',
        name: 'Stan magazynu na dzień',
        family: 'inventory',
        legacyCodes: ['KGK-CAN-SS-1'],
        description: 'Stan partii, ilość dostępna i wartość na wskazany dzień.',
        retentionYears: 5,
    },
    {
        code: 'canteen.supplier_invoice.v1',
        name: 'Faktura dostawcy',
        family: 'inventory',
        legacyCodes: [],
        description: 'Wersjonowany obraz faktury wraz z pozycjami i przyjęciem towaru.',
        retentionYears: 5,
    },
    {
        code: 'canteen.stock_movement.v1',
        name: 'Dokument ruchu magazynowego',
        family: 'inventory',
        legacyCodes: [],
        description: 'Przyjęcie, wydanie, zwrot, korekta albo przesunięcie magazynowe.',
        retentionYears: 5,
    },
    {
        code: 'canteen.production_batch.v1',
        name: 'Karta partii produkcyjnej',
        family: 'production',
        legacyCodes: [],
        description: 'Plan, zużyte partie, uzysk, straty i wydania posiłków.',
        retentionYears: 5,
    },
    {
        code: 'canteen.recipe_snapshot.v1',
        name: 'Wersja receptury i wartości odżywcze',
        family: 'production',
        legacyCodes: [],
        description: 'Niezmienny zapis receptury, alergenów i wartości odżywczych.',
        retentionYears: 5,
    },
    {
        code: 'canteen.menu_publication.v1',
        name: 'Publikacja jadłospisu',
        family: 'publication',
        legacyCodes: [],
        description: 'Opublikowany jadłospis z wariantami diet i alergenami.',
        retentionYears: 3,
    },
    {
        code: 'canteen.food_safety_check.v1',
        name: 'Kontrola HACCP',
        family: 'safety',
        legacyCodes: [],
        description: 'Pomiar, wynik, użyty szablon oraz działania korygujące.',
        retentionYears: 5,
    },
    {
        code: 'canteen.sanitation_run.v1',
        name: 'Wykonanie sanitacji',
        family: 'safety',
        legacyCodes: [],
        description: 'Wykonanie, weryfikacja i dowody procedury sanitarnej.',
        retentionYears: 5,
    },
    {
        code: 'canteen.food_sample_custody.v1',
        name: 'Łańcuch przekazania próbki żywności',
        family: 'safety',
        legacyCodes: [],
        description: 'Pobranie, przechowanie, przekazanie i utylizacja próbki.',
        retentionYears: 5,
    },
    {
        code: 'canteen.food_recall.v1',
        name: 'Wycofanie żywności',
        family: 'safety',
        legacyCodes: [],
        description: 'Zablokowanie partii, decyzja, działania i zamknięcie wycofania.',
        retentionYears: 10,
    },
    {
        code: 'canteen.export_manifest.v1',
        name: 'Manifest eksportu danych',
        family: 'export',
        legacyCodes: [],
        description: 'Dowód zakresu, formatu, danych źródłowych i sumy kontrolnej eksportu.',
        retentionYears: 5,
    },
];
export const canteenDmsDocumentCodeSchema = z.enum(canteenDmsDocumentDefinitions.map((item) => item.code));
/** A versioned, data-only print layout.  It can alter wording and the order or
 * visibility of already defined sections, but never modifies the source data. */
export const dmsDocumentTemplateSchema = z.object({
    definitionCode: canteenDmsDocumentCodeSchema,
    version: z.number().int().positive(),
    title: z.string().trim().min(1).max(240),
    introduction: z.string().trim().min(1).max(500),
    footerNote: z.string().trim().min(1).max(300),
    sectionOrder: z.array(z.string().regex(/^[a-z0-9-]{1,80}$/)).max(40),
    hiddenSectionIds: z.array(z.string().regex(/^[a-z0-9-]{1,80}$/)).max(40),
    updatedAt: dateTimeSchema,
});
export const updateDmsDocumentTemplateSchema = dmsDocumentTemplateSchema
    .omit({ version: true, updatedAt: true })
    .extend({ basedOnVersion: z.number().int().positive() });
export function defaultDmsDocumentTemplate(definitionCode) {
    const definition = canteenDmsDefinition(definitionCode);
    return {
        definitionCode,
        version: 1,
        title: definition.name,
        introduction: definition.description,
        footerNote: 'SAD5 DMS - dokument ze snapshotu danych.',
        sectionOrder: [],
        hiddenSectionIds: [],
        updatedAt: '1970-01-01T00:00:00.000Z',
    };
}
export function canteenDmsDefinition(code) {
    const definition = canteenDmsDocumentDefinitions.find((item) => item.code === code);
    if (!definition)
        throw new Error(`Nieznany typ dokumentu DMS: ${code}`);
    return definition;
}
export const registerDmsDocumentSchema = z.object({
    contractVersion: dmsContractVersionSchema,
    institutionId: uuidSchema,
    source: z.object({
        domain: z.literal('canteen'),
        aggregateType: z.string().min(1).max(100),
        aggregateId: z.string().min(1).max(200),
        aggregateRevision: z.number().int().positive(),
    }),
    definitionCode: canteenDmsDocumentCodeSchema,
    title: z.string().trim().min(1).max(240),
    period: z
        .object({ startsOn: dateSchema, endsOn: dateSchema })
        .refine((period) => period.startsOn <= period.endsOn, {
        message: 'Początek okresu nie może być późniejszy niż koniec.',
        path: ['endsOn'],
    })
        .nullable()
        .default(null),
    snapshot: z.record(z.string(), z.unknown()),
    requestedBy: uuidSchema,
    requestedAt: dateTimeSchema,
    idempotencyKey: z.string().min(16).max(200),
});
export const issueCanteenDmsDocumentSchema = z
    .object({
    startsOn: dateSchema,
    endsOn: dateSchema,
})
    .refine((period) => period.startsOn <= period.endsOn, {
    message: 'Początek okresu nie może być późniejszy niż koniec.',
    path: ['endsOn'],
});
export const dmsDocumentVersionSchema = z.object({
    id: uuidSchema,
    documentId: uuidSchema,
    version: z.number().int().positive(),
    snapshotHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    issuedAt: dateTimeSchema,
});
export const dmsDocumentVersionNumberSchema = z.coerce
    .number()
    .int()
    .positive()
    .max(100_000);
export const dmsDocumentSummarySchema = z.object({
    id: uuidSchema,
    institutionId: uuidSchema,
    definitionCode: canteenDmsDocumentCodeSchema,
    title: z.string(),
    state: dmsDocumentStateSchema,
    sourceAggregateType: z.string(),
    sourceAggregateId: z.string(),
    period: z.object({ startsOn: dateSchema, endsOn: dateSchema }).nullable(),
    currentVersion: dmsDocumentVersionSchema,
    createdAt: dateTimeSchema,
});
/** Immutable payload exported for a document's current version. */
export const dmsDocumentExportSchema = z.object({
    exportVersion: z.literal('1'),
    document: dmsDocumentSummarySchema,
    version: dmsDocumentVersionSchema,
    snapshot: z.record(z.string(), z.unknown()),
});
